import {
  ContentLayout,
  HeaderBreadcrumbs,
  Loader,
  AutocompleteWithEndAdornmentChip,
  LimitedTag,
} from "../../../shared";
import * as React from "react";
import {
  Button,
  Stack,
  Card,
  Typography,
  Grid,
  Chip,
  TextField,
  createFilterOptions,
  Checkbox,
  ListItemButton,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import InputControl from "../../../shared/components/InputControl";
import { useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import SimpleReactValidator from "simple-react-validator";
import moment from "moment";
import { useSurcharges } from "../hooks";
import { priceValidator } from "../../../utils";

const ToggleButtonStyled = styled(ToggleButton)({
  "&.Mui-selected, &.Mui-selected:hover": {
    color: "white",
    backgroundColor: "#1560D4",
  },
});

export const AddOrEditSurCharges = () => {
  const { id } = useParams();
  const {
    state,
    onSaveSurcharges,
    onChange,
    globalData,
    onDeleteStoreSelect,
    onUpdateSurcharges,
    isEditable,
    isCreateVisible,
    taxState,
    onDeleteChannelSelect,
  } = useSurcharges({ load: false, SurchargesId: id, isList: true });
  const taxStateFilter = taxState.taxGroup.listData?.map((i) => {
    return { label: i.name, value: i.id };
  });
  const [, forceUpdate] = React.useState();
  const validator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      validators: {
        price: priceValidator,
        endTime: {
          message: "The end time should greater than start time",

          rule: (value) => {
            const { startTime, endTime } = value;
            return moment(startTime, "HH:mm:ss").isBefore(
              moment(endTime, "HH:mm:ss"),
            );
          },
        },
      },
    }),
  );
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => option.name,
  });

  const onSubmit = () => {
    if (validator.current.allValid()) {
      id ? onUpdateSurcharges(id) : onSaveSurcharges();
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };
  const SaleType =
    globalData?.sale_type?.map((item) => {
      return { id: item?.value, name: item?.label };
    }) || [];

  const cetagoryOptions =
    globalData?.sale_type?.map((item) => {
      return { id: item?.value, name: item?.label };
    }) || [];

  const ALLOWED_SALES_CHANNELS = [100, 101];

  const SaleChannels =
    globalData?.sale_channels
      ?.filter((item) => ALLOWED_SALES_CHANNELS.includes(item.value))
      ?.map((item) => ({
        id: item.value,
        name: item.label,
      })) || [];

  if (state.isBusy) {
    return <Loader />;
  }
  return (
    <>
      <ContentLayout
        title={id ? state.name : "New Surcharge"}
        breadcrumbs={
          id ? (
            <>
              <HeaderBreadcrumbs
                links={[
                  { name: "Dashboard", href: "/" },
                  { name: "Surcharges", href: "/surcharges" },
                  { name: state.name },
                ]}
              />
            </>
          ) : (
            <>
              <HeaderBreadcrumbs
                links={[
                  { name: "Dashboard", href: "/" },
                  { name: "Surcharges", href: "/surcharges" },
                  { name: "Create" },
                ]}
              />
            </>
          )
        }
        rightContent={
          <>
            {isEditable && isCreateVisible && (
              <Button
                onClick={() => onSubmit()}
                type="button"
                size="medium"
                variant="contained"
                disabled={state.isSaveButtonBusy || !state.isFormChanged}
              >
                {id ? "Update" : "Save"}
              </Button>
            )}
          </>
        }
      >
        <Card sx={{ p: "32px 24px", mb: "24px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ width: "150px" }}
              >
                Surcharge Details
              </Typography>
            </Grid>
            <Grid item sm={8}>
              <Stack width={"100%"} gap="20px">
                <InputControl
                  name="name"
                  label="Title"
                  value={state.data.name}
                  onChange={onChange}
                  required
                  error={validator.current.message(
                    "Title",
                    state.data.name,
                    "required",
                  )}
                  helperText={validator.current.message(
                    "Title",
                    state.data.name,
                    "required",
                  )}
                />
                <Stack
                  sx={{
                    flexDirection: "row",
                    gap: "20px",
                  }}
                >
                  <InputControl
                    type="select"
                    name="category"
                    label="Category *"
                    value={state.data.category}
                    optionValue={globalData?.surcharge?.category ?? []}
                    width="100%"
                    onChange={onChange}
                    error={validator.current.message(
                      "Category",
                      state.data.category,
                      "required",
                    )}
                    helperText={validator.current.message(
                      "Category",
                      state.data.category,
                      "required",
                    )}
                  />
                  <InputControl
                    type="dropdown"
                    disableClearable
                    options={taxStateFilter}
                    getOptionLabel={(option) => option.label}
                    value={
                      (taxStateFilter &&
                        state.data.tax_group_id &&
                        taxStateFilter?.find(
                          (c) => c.value === state.data.tax_group_id,
                        )) ||
                      null
                    }
                    onChange={(e, newValue) => {
                      onChange({
                        target: {
                          name: "tax_group_id",
                          value: newValue.value,
                        },
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tax Group"
                        required
                        error={validator.current.message(
                          "Tax Group",
                          state.data.tax_group_id,
                          "required",
                        )}
                        helperText={validator.current.message(
                          "Tax Group",
                          state.data.tax_group_id,
                          "required",
                        )}
                      />
                    )}
                  />
                </Stack>
                <Stack direction="row" gap="20px">
                  <Stack width="100%">
                    <>
                      <AutocompleteWithEndAdornmentChip
                        name="channel_id"
                        label="Sales Channel"
                        options={SaleChannels ?? []}
                        value={state?.data?.channel_id ?? []}
                        multiple
                        disableCloseOnSelect
                        groupBy={() => "All"}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        handleChangeFilter={({ value }) => {
                          console.log(value);

                          onChange({
                            target: {
                              name: "channel_id",
                              value,
                            },
                          });
                        }}
                        limitedTag={(value) => (
                          <LimitedTag
                            labelKey="name"
                            value={value}
                            autocompleteWidth="220px"
                            labelWidth="110px"
                          />
                        )}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.id}>
                            <Checkbox
                              size="small"
                              checked={selected}
                              sx={{ mr: 1 }}
                            />
                            {option.name}
                          </li>
                        )}
                        renderGroup={(params) => {
                          const allSelected =
                            state?.data?.channel_id?.length ===
                            (SaleChannels?.length ?? 0);

                          return (
                            <React.Fragment key={params.group}>
                              {params.key === 0 && (
                                <ListItemButton
                                  disableGutters
                                  onClick={() =>
                                    onChange({
                                      target: {
                                        name: "channel_id",
                                        value: allSelected ? [] : SaleChannels,
                                      },
                                    })
                                  }
                                >
                                  <Checkbox
                                    size="small"
                                    checked={allSelected}
                                  />
                                  <Typography>All</Typography>
                                </ListItemButton>
                              )}
                              <ListItemText>{params.children}</ListItemText>
                            </React.Fragment>
                          );
                        }}
                        sx={{ minWidth: 220, width: "100%" }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Sales Channel"
                            placeholder="Sales Channel"
                            required
                            error={validator.current.message(
                              "Sales Channel",
                              state.data.channel_id,
                              "required",
                            )}
                            helperText={validator.current.message(
                              "Sales Channel",
                              state.data.channel_id,
                              "required",
                            )}
                          />
                        )}
                      />
                    </>
                    <Stack
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "12px",
                        marginTop: 2,
                      }}
                    >
                      {state?.data?.channel_id?.map((data, index) => (
                        <Chip
                          key={index}
                          label={
                            <Typography variant="body2" fontSize="14px">
                              {data?.name}
                            </Typography>
                          }
                          onDelete={() => {
                            onDeleteChannelSelect(data);
                          }}
                          sx={{
                            backgroundColor: "#F2F2F2",
                            height: "30px",
                            "& .MuiChip-deleteIcon": {
                              color: "#CCCCCC",
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                  <Stack width="100%">
                    <>
                      <AutocompleteWithEndAdornmentChip
                        name="sale_type_id"
                        label="Sale Type"
                        options={SaleType ?? []}
                        value={state?.data?.sale_type_id ?? []}
                        multiple
                        disableCloseOnSelect
                        groupBy={() => "All"}
                        handleChangeFilter={({ value }) => {
                          console.log(value);

                          onChange({
                            target: {
                              name: "sale_type_id",
                              value,
                            },
                          });
                        }}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        limitedTag={(value) => (
                          <LimitedTag
                            labelKey="name"
                            value={value}
                            autocompleteWidth="220px"
                            labelWidth="110px"
                          />
                        )}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.id}>
                            <Checkbox
                              size="small"
                              checked={selected}
                              sx={{ mr: 1 }}
                            />
                            {option.name}
                          </li>
                        )}
                        renderGroup={(params) => {
                          const allSelected =
                            state?.data?.sale_type_id?.length ===
                            (SaleType?.length ?? 0);

                          return (
                            <React.Fragment key={params.group}>
                              {params.key === 0 && (
                                <ListItemButton
                                  disableGutters
                                  onClick={() =>
                                    onChange({
                                      target: {
                                        name: "sale_type_id",
                                        value: allSelected ? [] : SaleType,
                                      },
                                    })
                                  }
                                >
                                  <Checkbox
                                    size="small"
                                    checked={allSelected}
                                  />
                                  <Typography>All</Typography>
                                </ListItemButton>
                              )}
                              <ListItemText>{params.children}</ListItemText>
                            </React.Fragment>
                          );
                        }}
                        sx={{ minWidth: 220, width: "100%" }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Sale Type"
                            placeholder="Sale Type"
                            required
                            error={validator.current.message(
                              "Sale Type",
                              state.data.sale_type_id,
                              "required",
                            )}
                            helperText={validator.current.message(
                              "Sale Type",
                              state.data.sale_type_id,
                              "required",
                            )}
                          />
                        )}
                      />
                    </>
                    <Stack
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "12px",
                        marginTop: 2,
                      }}
                    >
                      {state?.data?.sale_type_id?.map((data, index) => (
                        <Chip
                          key={index}
                          label={
                            <Typography variant="body2" fontSize="14px">
                              {data?.name}
                            </Typography>
                          }
                          onDelete={() => {
                            onDeleteStoreSelect(data);
                          }}
                          sx={{
                            backgroundColor: "#F2F2F2",
                            height: "30px",
                            "& .MuiChip-deleteIcon": {
                              color: "#CCCCCC",
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    flexDirection: "row",
                    gap: "20px",
                  }}
                >
                  <ToggleButtonGroup
                    color="primary"
                    value={state.data.fee_type}
                    exclusive
                    sx={{
                      maxHeight: "48px",
                    }}
                    onChange={(e, v) =>
                      onChange({
                        target: {
                          name: "fee_type",
                          value: e.target.value,
                        },
                      })
                    }
                    aria-label="Platform"
                    className="custom-pos"
                  >
                    {globalData.discount_mode?.map((item, i) => (
                      <ToggleButtonStyled
                        key={i}
                        sx={{
                          width: "102px",
                          fontSize: "14px",
                          fontWeight: "400",
                          color: "#636363",
                        }}
                        value={item.value}
                      >
                        {item.label}
                      </ToggleButtonStyled>
                    ))}
                  </ToggleButtonGroup>
                  <InputControl
                    name="amount"
                    label={
                      state.data.fee_type === "percentage"
                        ? "Value % (In GST)"
                        : "Value $ (In GST)"
                    }
                    type="number"
                    value={state.data.amount}
                    onChange={onChange}
                    required
                    isDisabledKeys={["-"]}
                    error={validator.current.message(
                      "Charge",
                      state.data.amount,
                      "required|price",
                    )}
                    helperText={validator.current.message(
                      "Charge",
                      state.data.amount,
                      "required|price",
                    )}
                  />
                </Stack>
                <Stack
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  <Stack>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Auto apply to in-store orders
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight={400}
                      color="#636363"
                    >
                      Enable this to automatically apply the charge to in-store
                      orders, if applicable.
                    </Typography>
                  </Stack>
                  <Switch
                    name="status"
                    defaultChecked={state.data.in_store_auto_apply}
                    checked={state.data.in_store_auto_apply}
                    onChange={(e, v) =>
                      onChange({
                        target: {
                          name: "in_store_auto_apply",
                          value: v,
                        },
                      })
                    }
                  />
                </Stack>
                <Stack
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  <Stack>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Apply Charge Only on Public Holidays
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight={400}
                      color="#636363"
                    >
                      Enable this to apply the surcharge only on days marked as
                      public holidays. Public holidays can be configured under
                      Stores → Opening Hours in the Super Admin Panel.
                    </Typography>
                  </Stack>
                  <Switch
                    name="status"
                    defaultChecked={state.data.public_holiday_fee_enabled}
                    checked={state.data.public_holiday_fee_enabled}
                    onChange={(e, v) =>
                      onChange({
                        target: {
                          name: "public_holiday_fee_enabled",
                          value: v,
                        },
                      })
                    }
                  />
                </Stack>
                <Stack
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  <Stack>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Is this Surcharge refundable?
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight={400}
                      color="#636363"
                    >
                      Enable this option to allow the surcharge to be refunded
                      when the order is refunded.
                    </Typography>
                  </Stack>

                  <Switch
                    name="status"
                    defaultChecked={state.data.is_it_refundable}
                    checked={state.data.is_it_refundable}
                    onChange={(e, v) =>
                      onChange({
                        target: {
                          name: "is_it_refundable",
                          value: v,
                        },
                      })
                    }
                  />
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Card>
      </ContentLayout>
    </>
  );
};
